import React, { useEffect, useState } from "react";
import {
  Row,
  Label,
  Input,
  FormFeedback,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import * as Yup from "yup";
import Select from "react-select";
import { useFormik } from "formik";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";

import { NotificationMethodEnum, RoleEnum } from "../../enums";
import { ActivityButton } from "../../components/Common";
import {
  State,
  SendNotificationV3Request,
  LocalizedProperty,
  TemplateDto,
} from "../../interfaces";
import {
  resetSendNotificationV3,
  sendNotificationV3,
} from "../../store/actions";
import { TemplateSelect } from "../../components";

type TProps = {
  mode: "role" | "grade" | "complain" | "user";
  role?: [
    {
      id: string;
      name: LocalizedProperty;
    }
  ];
  grade?: [
    {
      id: string;
      name: LocalizedProperty;
    }
  ];
  complain?: {
    id: string;
    message: string;
  };
  user?: [
    {
      id: string;
      name: LocalizedProperty;
    }
  ];
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const SendPushNotificationModal: React.FC<TProps> = ({
  mode,
  role,
  grade,
  complain,
  user,
  show,
  onCloseClick,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  //#region internal state
  const [sendMethod, setSendMethod] = useState<NotificationMethodEnum>(
    NotificationMethodEnum.WHATSAPP
  );
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  //#endregion

  //#region redux state
  const { response: templates } = useSelector(
    (state: any): State<TemplateDto[]> => state.template.templates
  );
  //#endregion

  //#region notification
  const {
    loading: loadingSendNotification,
    success: successSendNotification,
    error: errorSendNotification,
  } = useSelector(
    (state: any): State<any> => state.notification.sendNotificationV3
  );

  /**
   * Formik handling
   */
  const notificationForm = useFormik<SendNotificationV3Request>({
    enableReinitialize: true,
    initialValues: {
      mode: mode,
      ...(mode === "role" && { role: role?.map((r) => r.id) }),
      ...(mode === "grade" && { grade: grade?.map((g) => g.id) }),
      ...(mode === "complain" && { complain: complain?.id }),
      ...(mode === "user" && { user: user?.map((u) => u.id) }),
      title: "",
      arabicTitle: "",
      body: "",
      arabicBody: "",
      method: NotificationMethodEnum.WHATSAPP,
      templateId: null,
    },

    validationSchema: Yup.object<SendNotificationV3Request>({
      mode: Yup.string().oneOf(["role", "grade", "complain", "user"]),
      ...(mode === "role" && {
        role: Yup.array()
          .min(
            1,
            t("minLengthArray", { minLengthArray: 1, displayName: "role" })
          )
          .required(t("isNotEmpty", { displayName: "role" })),
      }),
      ...(mode === "grade" && {
        grade: Yup.array()
          .min(
            1,
            t("minLengthArray", { minLengthArray: 1, displayName: "grade" })
          )
          .required(t("isNotEmpty", { displayName: "grade" })),
      }),
      ...(mode === "complain" && {
        complain: Yup.string().required(
          t("isNotEmpty", { displayName: "complain" })
        ),
      }),
      ...(mode === "user" && {
        user: Yup.array()
          .min(
            1,
            t("minLengthArray", { minLengthArray: 1, displayName: "user" })
          )
          .required(t("isNotEmpty", { displayName: "user" })),
      }),
      title: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "title" }))
            .max(50, t("maxLength", { maxLength: 50, displayName: "title" })),
      }),
      arabicTitle: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicTitle" }))
            .max(
              50,
              t("maxLength", { maxLength: 50, displayName: "arabicTitle" })
            ),
      }),
      body: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "body" }))
            .max(255, t("maxLength", { maxLength: 255, displayName: "body" })),
      }),
      arabicBody: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicBody" }))
            .max(
              255,
              t("maxLength", { maxLength: 255, displayName: "arabicBody" })
            ),
      }),
      method: Yup.string().required(t("isNotEmpty", { displayName: "method" })),
      templateId: Yup.string().when("method", {
        is: NotificationMethodEnum.WHATSAPP,
        then: (schema) =>
          schema.required(t("isNotEmpty", { displayName: "template" })),
      }),
    }),
    onSubmit: (values) => {
      if (!values.method) return;

      if (
        values.method == NotificationMethodEnum.WHATSAPP &&
        !values.templateId
      )
        return;

      if (mode === "role" && role && role.length > 0) {
        const _role = values.role?.map((v: any) => v);
        if (!_role || _role.length === 0) return;
        dispatch(
          sendNotificationV3({ ...values, role: _role, method: sendMethod })
        );
      }
      if (mode === "grade" && grade && grade.length > 0) {
        const _grade = values.grade?.map((v: any) => v);
        if (!_grade || _grade.length === 0) return;
        dispatch(
          sendNotificationV3({ ...values, grade: _grade, method: sendMethod })
        );
      }
      if (mode === "complain" && complain?.id) {
        const _complain = values.complain;
        if (!_complain || _complain.length === 0) return;
        dispatch(
          sendNotificationV3({
            ...values,
            complain: _complain,
            method: sendMethod,
          })
        );
      }
      if (mode === "user" && user && user.length > 0) {
        const _user = values.user?.map((v: any) => v);
        if (!_user || _user.length === 0) return;

        dispatch(
          sendNotificationV3({ ...values, user: _user, method: sendMethod })
        );
      }
    },
  });

  useEffect(() => {
    if (!loadingSendNotification && successSendNotification) {
      toast.success(t("notificationSentSuccessfully"), {
        autoClose: 3000,
      });
      onClose();
    }
    if (!loadingSendNotification && errorSendNotification) {
      toast.error(errorSendNotification, {
        autoClose: 3000,
      });
    }
  }, [loadingSendNotification, errorSendNotification, successSendNotification]);

  useEffect(() => {
    notificationForm.setFieldValue("method", sendMethod);
  }, [sendMethod]);

  useEffect(() => {
    if (sendMethod === NotificationMethodEnum.WHATSAPP) {
      const _chooseTemplate = templates?.find(
        (t) => t.sid === selectedTemplate?.value
      );
      if (_chooseTemplate?.types["twilio/text"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/text"]["body"]
        );
      }
      if (_chooseTemplate?.types["twilio/call-to-action"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/call-to-action"]["body"]
        );
      }
      notificationForm.setFieldValue("templateId", _chooseTemplate?.sid);
    } else {
      notificationForm.setFieldValue("body", null);
      notificationForm.setFieldTouched("body", false);
    }
  }, [selectedTemplate, sendMethod]);

  //#endregion

  const onClose = () => {
    notificationForm.resetForm();
    onCloseClick(!show);
    dispatch(resetSendNotificationV3());
  };

  return (
    <Modal
      id="pushNotification"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
      size="lg"
    >
      <ToastContainer />

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          notificationForm.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {t("sendNotification")}
        </ModalHeader>
        {typeof errorSendNotification === "string" && (
          <Label className="invalid-feedback d-block">
            {errorSendNotification}
          </Label>
        )}

        <ModalBody>
          {mode === "role" && (
            <Row>
              <div className="mb-3 col">
                <Label htmlFor="title" className="form-label">
                  {t("role")}
                  <span className="text-danger">*</span>
                </Label>
                <Select
                  id="role"
                  placeholder={t("chooseRole")}
                  closeMenuOnSelect={false}
                  value={notificationForm.values.role}
                  onChange={(changedValue) =>
                    notificationForm.setFieldValue("role", changedValue)
                  }
                  isMulti={true}
                  options={
                    [
                      {
                        label: t("students"),
                        value: RoleEnum.STUDENT,
                      },
                      {
                        label: t("parents"),
                        value: RoleEnum.PARENT,
                      },
                      {
                        label: t("teachers"),
                        value: RoleEnum.TEACHER,
                      },
                    ] as any
                  }
                />
                {notificationForm.errors.role ? (
                  <FormFeedback type="invalid" style={{ display: "block" }}>
                    {notificationForm.errors.role}
                  </FormFeedback>
                ) : null}
              </div>
            </Row>
          )}
          {mode === "grade" && (
            <p className="mb-3 fs-14">
              <strong> {t("selectedGrades")}:&nbsp;</strong>
              {grade?.map((g) =>
                i18n.language === "ar" ? g.name.ar : g.name.en
              )}
            </p>
          )}
          {mode === "complain" && (
            <p className="mb-3 fs-14">
              <strong>{t("replyingToMessage")}:&nbsp;</strong>
              {complain?.message}
            </p>
          )}
          {mode === "user" && (
            <p className="mb-3 fs-14">
              <strong>{t("targetUsers")}:&nbsp;</strong>
              {user
                ?.map((u) =>
                  i18n.language === "ar" ? u.name.ar : u.name.en || t(" - ")
                )
                .join(", ")}
            </p>
          )}
          <Row>
            <div className="mb-3 col">
              <div>
                <div className="form-check form-check-inline">
                  <Input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadio4"
                    id="inlineRadio4"
                    value={NotificationMethodEnum.WHATSAPP}
                    checked={sendMethod == NotificationMethodEnum.WHATSAPP}
                    onChange={() => {
                      setSendMethod(NotificationMethodEnum.WHATSAPP);
                    }}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio4">
                    {t(NotificationMethodEnum.WHATSAPP)}
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <Input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadio3"
                    id="inlineRadio3"
                    value={NotificationMethodEnum.PUSH_NOTIFICATION}
                    checked={
                      sendMethod == NotificationMethodEnum.PUSH_NOTIFICATION
                    }
                    onChange={() => {
                      setSendMethod(NotificationMethodEnum.PUSH_NOTIFICATION);
                    }}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio3">
                    {t(NotificationMethodEnum.PUSH_NOTIFICATION)}
                  </label>
                </div>
              </div>
            </div>
          </Row>
          <Row>
            {sendMethod === NotificationMethodEnum.WHATSAPP && (
              <div className="mb-3 col">
                <TemplateSelect
                  placeholder="chooseTemplate"
                  isMulti={false}
                  closeMenuOnSelect={true}
                  value={selectedTemplate}
                  onChange={(newValue) => {
                    setSelectedTemplate(newValue);
                  }}
                ></TemplateSelect>
              </div>
            )}
          </Row>
          {sendMethod != NotificationMethodEnum.WHATSAPP && (
            <>
              <Row>
                <div className="mb-3 col">
                  <Label htmlFor="title" className="form-label">
                    {t("title")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    id="title"
                    placeholder={t("enterTitle")}
                    value={notificationForm.values.title || ""}
                    onChange={notificationForm.handleChange}
                    onBlur={notificationForm.handleBlur}
                    invalid={
                      notificationForm.touched.title &&
                      notificationForm.errors.title
                        ? true
                        : false
                    }
                  />
                  {notificationForm.touched.title &&
                  notificationForm.errors.title ? (
                    <FormFeedback type="invalid">
                      {notificationForm.errors.title}
                    </FormFeedback>
                  ) : null}
                </div>
              </Row>
              <Row>
                <div className="mb-3 col">
                  <Label htmlFor="arabicTitle" className="form-label">
                    {t("arabicTitle")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    id="arabicTitle"
                    placeholder={t("enterArabicTitle")}
                    value={notificationForm.values.arabicTitle || ""}
                    onChange={notificationForm.handleChange}
                    onBlur={notificationForm.handleBlur}
                    invalid={
                      notificationForm.touched.arabicTitle &&
                      notificationForm.errors.arabicTitle
                        ? true
                        : false
                    }
                  />
                  {notificationForm.touched.arabicTitle &&
                  notificationForm.errors.arabicTitle ? (
                    <FormFeedback type="invalid">
                      {notificationForm.errors.arabicTitle}
                    </FormFeedback>
                  ) : null}
                </div>
              </Row>
            </>
          )}
          <Row>
            <div className="mb-3 col">
              <Label htmlFor="body" className="form-label">
                {sendMethod === NotificationMethodEnum.WHATSAPP
                  ? t("Template")
                  : t("body")}
                {sendMethod != NotificationMethodEnum.WHATSAPP && (
                  <span className="text-danger">*</span>
                )}
              </Label>
              <Input
                disabled={sendMethod === NotificationMethodEnum.WHATSAPP}
                type="textarea"
                row="5"
                style={{ height: "150px" }}
                className="form-control"
                id="body"
                placeholder={
                  sendMethod === NotificationMethodEnum.WHATSAPP
                    ? ""
                    : t("enterMessageBody")
                }
                value={notificationForm.values.body || ""}
                onChange={notificationForm.handleChange}
                onBlur={notificationForm.handleBlur}
                invalid={
                  notificationForm.touched.body && notificationForm.errors.body
                    ? true
                    : false
                }
              />
              {notificationForm.touched.body && notificationForm.errors.body ? (
                <FormFeedback type="invalid">
                  {notificationForm.errors.body}
                </FormFeedback>
              ) : null}
            </div>
          </Row>
          {sendMethod != NotificationMethodEnum.WHATSAPP && (
            <Row>
              <div className="mb-3 col">
                <Label htmlFor="arabicBody" className="form-label">
                  {t("arabicBody")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  row="5"
                  className="form-control"
                  style={{ height: "150px" }}
                  id="arabicBody"
                  placeholder={t("enterMessageBodyInArabic")}
                  value={notificationForm.values.arabicBody || ""}
                  onChange={notificationForm.handleChange}
                  onBlur={notificationForm.handleBlur}
                  invalid={
                    notificationForm.touched.arabicBody &&
                    notificationForm.errors.arabicBody
                      ? true
                      : false
                  }
                />
                {notificationForm.touched.arabicBody &&
                notificationForm.errors.arabicBody ? (
                  <FormFeedback type="invalid">
                    {notificationForm.errors.arabicBody}
                  </FormFeedback>
                ) : null}
              </div>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title={t("send")}
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={loadingSendNotification}
            disabled={loadingSendNotification}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
